/**
 * AdminScheduleSelector - per-step instrument assignment (Sprint 3.4 REDO).
 *
 * The prototype right-center panel splits the protocol into 4 steps:
 * baseline, monitoring, tolerance, outcome. Each step row carries
 * checkboxes for the currently-selected instruments so the clinician
 * can decide which instruments fire at which step. The admin schedule
 * is the bridge between the instrument multiselect and the
 * `QuestionnaireResponse` preview.
 *
 * HC1 reproducibility: row order is fixed by `ADMIN_SCHEDULE_STEPS`.
 * HC2 token-only.
 * HC5 zero em / en dashes.
 */
import {
  ADMIN_SCHEDULE_STEPS,
  type AdminScheduleStepId,
  type PromInstrumentCode,
  type ScheduleAssignments,
} from './types';

export interface AdminScheduleSelectorProps {
  /** Closed instrument selection coming from the multiselect. */
  selectedInstruments: PromInstrumentCode[];
  /** Per-step instrument assignment map (controlled). */
  assignments: ScheduleAssignments;
  /** Update handler. Receives the next assignment map. */
  onChange: (next: ScheduleAssignments) => void;
}

export function AdminScheduleSelector({
  selectedInstruments,
  assignments,
  onChange,
}: AdminScheduleSelectorProps) {
  function toggle(stepId: AdminScheduleStepId, code: PromInstrumentCode) {
    const current = assignments[stepId];
    const exists = current.includes(code);
    const nextForStep = exists
      ? current.filter((c) => c !== code)
      : [...current, code];
    onChange({ ...assignments, [stepId]: nextForStep });
  }

  return (
    <section
      data-testid="admin-schedule-selector"
      aria-labelledby="admin-schedule-title"
      className="rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card"
    >
      <header>
        <h3
          id="admin-schedule-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          Administration Schedule
        </h3>
        <p className="mt-gutter-sm text-signal-sm text-text-secondary">
          Map selected instruments to protocol steps.
        </p>
      </header>
      {selectedInstruments.length === 0 ? (
        <p
          data-testid="admin-schedule-empty"
          className="mt-gutter-md rounded-signal-md border border-dashed border-surface-border bg-surface-muted p-gutter-md text-signal-sm text-text-tertiary"
        >
          Select at least one instrument to assign it to a schedule step.
        </p>
      ) : (
        <ul
          role="list"
          aria-label="Administration schedule steps"
          className="mt-gutter-md space-y-gutter-md"
        >
          {ADMIN_SCHEDULE_STEPS.map((step) => (
            <li
              key={step.id}
              data-testid={`admin-schedule-row-${step.id}`}
              className="rounded-signal-md border border-surface-border bg-surface-muted p-gutter-md"
            >
              <p className="text-signal-sm font-semibold text-text-primary">
                {step.label}
              </p>
              <div className="mt-gutter-sm flex flex-wrap gap-gutter-md">
                {selectedInstruments.map((code) => {
                  const active = assignments[step.id].includes(code);
                  const inputId = `schedule-${step.id}-${code.toLowerCase()}`;
                  return (
                    <label
                      key={code}
                      htmlFor={inputId}
                      className="flex cursor-pointer items-center gap-gutter-sm text-signal-xs text-text-secondary"
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        data-testid={`schedule-checkbox-${step.id}-${code}`}
                        checked={active}
                        onChange={() => toggle(step.id, code)}
                        className="h-3.5 w-3.5 rounded-signal-sm border-surface-border text-signal-primary-500 focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                      />
                      <span className="font-semibold text-text-primary">
                        {code}
                      </span>
                    </label>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
